import { isValid, isWithinInterval } from 'date-fns'
import React from 'react'
import { dateAdjustedForTimezone } from '@opallabs/lambda'
import { momentHasScheduledAt, PartialMoment } from '../momentTypes'
import { WeekViewSortType } from '../types'
import { makeCategoryGetter } from './momentGetters'
import {
  addValueToMap,
  createMomentMap,
  formatDateKey,
  makeMapKey,
  MomentMap,
} from './momentMap'

export { ContentMapProvider, useContentMap } from './contentMap'
export type { ContentMap, ContentSpanItem } from './contentMap'

interface MomentMapProviderParams {
  interval: {
    start: Date
    end: Date
  }
  moments: Record<string, PartialMoment>
  groupBy: WeekViewSortType
  // Parent entity used for more specific grouping
  //  e.g. User selected label set
  parentCategory?: string
}

type MomentMapContextValue = {
  categoryCounts: Map<string | null, number>
  dayCounts: Map<string, number>
  getMoment: (id: string) => PartialMoment | undefined
  momentMap: MomentMap
}

export const MomentMapContext = React.createContext<MomentMapContextValue>({
  categoryCounts: new Map(),
  dayCounts: new Map(),
  getMoment: () => undefined,
  momentMap: createMomentMap(),
})

const incrementMapCount = (
  map: Map<string | null, number>,
  key: string | null
) => {
  const currentCount = map.get(key) ?? 0
  map.set(key, currentCount + 1)
}

export const MomentMapProvider: React.FC<
  React.PropsWithChildren<MomentMapProviderParams>
> = ({ children, groupBy, interval, moments, parentCategory }) => {
  const {
    map: momentMap,
    dayCounts,
    categoryCounts,
  } = React.useMemo(() => {
    const categoryCounts = new Map<string | null, number>()
    const dayCounts = new Map<string, number>()
    const map = createMomentMap()
    if (!moments) return { categoryCounts, dayCounts, map }

    const categoryGetter = makeCategoryGetter(groupBy)

    Object.values(moments).forEach((moment) => {
      if (!momentHasScheduledAt(moment)) return
      const date = dateAdjustedForTimezone(moment.scheduled_at)

      if (!isWithinInterval(date, interval)) {
        return
      }

      const momentCategories = categoryGetter(moment, parentCategory)

      if (date === null || !isValid(date)) {
        throw new Error(
          'Moment has invalid `scheduled_at`, unable to create map.'
        )
      }

      // Update moment count for date
      const dateKey = formatDateKey(date)
      incrementMapCount(dayCounts, dateKey)

      // Assign moment.id to relevant categories
      if (momentCategories === null) {
        const key = makeMapKey(date, null)
        incrementMapCount(categoryCounts, null)
        addValueToMap(map, key, moment.id)
      } else {
        momentCategories?.forEach((categoryId) => {
          const key = makeMapKey(date, categoryId)
          incrementMapCount(categoryCounts, categoryId)
          addValueToMap(map, key, moment.id)
        })
      }
    })

    return {
      categoryCounts,
      dayCounts,
      map,
    }
  }, [moments, groupBy, interval, parentCategory])

  return (
    <MomentMapContext.Provider
      value={{
        categoryCounts,
        dayCounts,
        getMoment: (id: string) => moments[id],
        momentMap,
      }}
    >
      {children}
    </MomentMapContext.Provider>
  )
}

export const useMomentMap = (): MomentMapContextValue => {
  const context = React.useContext(MomentMapContext)

  if (context === undefined) {
    throw new Error(
      'useMomentMap must be called from within an MomentMapProvider.'
    )
  }

  return context
}
